// src/components/admin/SecurityAlertsWidget.tsx
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertTriangle,
  Shield,
  XCircle,
  RefreshCw,
  ChevronRight,
  Clock
} from 'lucide-react';
import { loginLogsAPI } from '@/services/api';
import { useNavigate } from 'react-router-dom';

interface RecentLog {
  id: number;
  username: string;
  ipAddress: string;
  status: string;
  loginTimestamp: string;
}

export function SecurityAlertsWidget() {
  const [recentFailedLogs, setRecentFailedLogs] = useState<RecentLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const fetchRecentFailed = async () => {
    try {
      setIsLoading(true);
      const logs = await loginLogsAPI.getRecent(15);
      const failed = logs.filter((l: any) => l.status !== 'SUCCESS').slice(0, 5);
      setRecentFailedLogs(failed);
    } catch (error) {
      console.error('Failed to fetch recent logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentFailed();
    const interval = setInterval(fetchRecentFailed, 30000);
    return () => clearInterval(interval);
  }, []);

  const getRelativeTime = (dateStr: string) => {
    const now = new Date();
    const past = new Date(dateStr);
    const diffMins = Math.floor((now.getTime() - past.getTime()) / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    return `${Math.floor(diffMins / 60)}h ago`;
  };

  if (recentFailedLogs.length === 0) {
    return (
      <Card className="border-l-4 border-l-green-500">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Shield className="w-4 h-4 text-green-500" />
              Security Status
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchRecentFailed}
              disabled={isLoading}
            >
              <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <Shield className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-sm text-green-700 dark:text-green-400 font-medium">
              All Clear
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              No failed logins in the last 15 minutes
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-l-4 border-l-red-500">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500 animate-pulse" />
            Security Alerts
            <Badge variant="destructive" className="ml-1">
              {recentFailedLogs.length}
            </Badge>
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchRecentFailed}
            disabled={isLoading}
          >
            <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {recentFailedLogs.map((log) => (
          <div
            key={log.id}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-600" />
                <span className="font-medium text-sm text-gray-900 dark:text-white">
                  {log.username}
                </span>
              </div>
              <span className="text-xs text-red-600 dark:text-red-400">
                {getRelativeTime(log.loginTimestamp)}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
              <Clock className="w-3 h-3" />
              <span className="font-mono">{log.ipAddress}</span>
            </div>
          </div>
        ))}

        <Button
          variant="outline"
          size="sm"
          className="w-full mt-2"
          onClick={() => navigate('/admin/login-logs')}
        >
          View All Login Logs
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}